/**
 * Local type definitions for the 2026-07-13 business-owner-walkthrough backend surface
 * (`backend/BACKEND_ARCHITECTURE.md` §10) — branches, staff/invite management, supplier/
 * customer directories, inventory depth (category/unit_of_measure/supplier/barcodes/
 * write-off), purchase orders, stock transfers, reports, and sync-conflict resolution.
 *
 * **Deliberately NOT added to `@shop-ledger/shared-types`.** Three other agents
 * (`pos-pwa`, `platform-admin`, `ecommerce-store`) are working in this same tree at the
 * same time, and this task's ground rule is "only touch files under
 * `frontend/apps/dashboard-admin/`" — editing a shared package file (even an existing
 * `entities/*.ts`/`api/*.ts` file to add a field, or an `index.ts` barrel to export a new
 * one) risks clobbering a concurrent edit from another agent. Every shape below is
 * therefore read directly from `backend/app/schemas/*.py` / `backend/app/api/v1/*.py`
 * (verified against actual route code, not summarized) and declared locally, reusing
 * `@shop-ledger/shared-types`' stable primitives (`UUID`, `DecimalString`,
 * `ISODateString`, `ISODateTimeString`, `Money`) which this task did not change. Some
 * types below (`InventoryItemOut`, `LedgerEntryOut`, `InvoiceOrBillCreate/Out`) are
 * corrected/extended supersets of the now-stale equivalents in `shared-types` — import
 * from HERE in this app, not from `@shop-ledger/shared-types`, for anything touched by
 * this pass.
 */
import type { DecimalString, ISODateString, ISODateTimeString, UUID } from "@shop-ledger/shared-types";
import type { AccountType, InvoiceOrBillStatus, InvoiceOrBillType, RecurrenceInterval } from "@shop-ledger/shared-types";

// ---------------------------------------------------------------------------
// Branches — backend/app/schemas/tenant.py, backend/app/api/v1/branches.py
// ---------------------------------------------------------------------------

export interface BranchOut {
  id: UUID;
  name: string;
  location_address: string | null;
  is_active: boolean;
}

export interface BranchCreate {
  name: string;
  location_address?: string | null;
}

export interface BranchUpdate {
  name?: string;
  location_address?: string | null;
  is_active?: boolean;
}

// ---------------------------------------------------------------------------
// Users / staff & invite tokens — backend/app/schemas/user.py, api/v1/users.py,
// AcceptInviteRequest/Response live in backend/app/schemas/auth.py but are re-declared
// here since they travel with the invite flow, not auth/session management.
// ---------------------------------------------------------------------------

export interface UserManagementOut {
  id: UUID;
  email: string;
  role: string;
  assigned_branch_id: UUID | null;
  is_active: boolean;
  is_deleted: boolean;
  created_at: ISODateTimeString;
}

export interface UserCreate {
  email: string;
  role: string;
  assigned_branch_id?: UUID | null;
}

export interface UserUpdate {
  role?: string;
  assigned_branch_id?: UUID | null;
  is_active?: boolean;
  /** Set true to explicitly clear `assigned_branch_id` to NULL — distinct from omitting
   * the field, which leaves it unchanged. */
  clear_assigned_branch?: boolean;
}

export interface UserInviteOut {
  user: UserManagementOut;
  /** Shown exactly once — the backend only ever persists a sha256 hash of this. This IS
   * the invite, since no email infrastructure exists (`backend/BACKEND_ARCHITECTURE.md`
   * §10.1). */
  setup_token: string;
  setup_token_expires_at: ISODateTimeString;
  note: string;
}

export interface AcceptInviteRequest {
  token: string;
  new_password: string;
}

export interface AcceptInviteResponse {
  user_id: UUID;
  email: string;
  detail: string;
}

// ---------------------------------------------------------------------------
// Suppliers — backend/app/schemas/inventory.py (SupplierCreate/Out)
// ---------------------------------------------------------------------------

export interface SupplierOut {
  id: UUID;
  name: string;
  phone: string | null;
  email: string | null;
  is_deleted: boolean;
}

export interface SupplierCreate {
  name: string;
  phone?: string | null;
  email?: string | null;
}

// ---------------------------------------------------------------------------
// Customers — backend/app/schemas/customer.py
// ---------------------------------------------------------------------------

export interface CustomerOut {
  id: UUID;
  name: string;
  phone: string | null;
  email: string | null;
  notes: string | null;
  location: string | null;
  tin: string | null;
  custom_fields: Record<string, string>;
  is_deleted: boolean;
}

export interface CustomerCreate {
  name: string;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
  location?: string | null;
  tin?: string | null;
  custom_fields?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Generated documents (quotation/invoice/receipt snapshots, Documents page) —
// backend/app/schemas/documents.py. Deliberately separate from InvoiceOrBill —
// never posts to the ledger.
// ---------------------------------------------------------------------------

export interface IssuerSnapshot {
  address: string | null;
  phone: string | null;
  email: string | null;
  tin: string | null;
  custom_fields: Record<string, string>;
}

export interface ClientSnapshot {
  name: string;
  phone: string | null;
  location: string | null;
  email: string | null;
  tin: string | null;
  custom_fields: Record<string, string>;
}

export interface DocLineSnapshot {
  inventory_item_id: UUID | null;
  name: string;
  detail: string | null;
  quantity: DecimalString;
  unit_price: DecimalString;
  image_url: string | null;
}

export type GeneratedDocumentStatus = "DRAFT" | "FINAL";
export type GeneratedDocumentType = "QUOTATION" | "INVOICE" | "RECEIPT";
export type GeneratedDocumentTemplate = "classic" | "modern" | "minimal" | "bold" | "gradient";

export interface GeneratedDocumentCreate {
  name: string;
  status: GeneratedDocumentStatus;
  doc_type: GeneratedDocumentType;
  template: GeneratedDocumentTemplate;
  branch_id?: UUID | null;
  doc_number: string;
  issue_date: ISODateString;
  secondary_date?: ISODateString | null;
  issuer: IssuerSnapshot;
  client: ClientSnapshot;
  lines: DocLineSnapshot[];
  discount?: DecimalString | null;
  tax_percent?: DecimalString | null;
  notes?: string | null;
  show_images: boolean;
  show_logo: boolean;
  show_watermark: boolean;
  currency: string;
}

export type GeneratedDocumentUpdate = GeneratedDocumentCreate;

export interface GeneratedDocumentOut {
  id: UUID;
  branch_id: UUID | null;
  created_by_user_id: UUID;
  name: string;
  status: GeneratedDocumentStatus;
  doc_type: GeneratedDocumentType;
  template: GeneratedDocumentTemplate;
  doc_number: string;
  issue_date: ISODateString;
  secondary_date: ISODateString | null;
  issuer: IssuerSnapshot;
  client: ClientSnapshot;
  lines: DocLineSnapshot[];
  discount: DecimalString | null;
  tax_percent: DecimalString | null;
  notes: string | null;
  show_images: boolean;
  show_logo: boolean;
  show_watermark: boolean;
  currency: string;
  is_deleted: boolean;
  created_at: ISODateTimeString;
  updated_at: ISODateTimeString;
}

// ---------------------------------------------------------------------------
// Inventory depth — extends the now-stale shared-types InventoryItem shapes with
// category/unit_of_measure/supplier_id (backend/app/schemas/inventory.py), plus
// barcodes and write-off.
// ---------------------------------------------------------------------------

export interface InventoryItemOut {
  id: UUID;
  branch_id: UUID;
  sku: string;
  name: string;
  description: string | null;
  retail_price: DecimalString;
  wholesale_price: DecimalString | null;
  current_stock: DecimalString;
  low_stock_threshold: DecimalString;
  /** Auto-managed mirror of this item's ONE active batch (multi-batch tracking, backend
   * `app/services/batch_service.py`) — non-null only while the item has exactly one active
   * batch; null once it has zero or several (see `nearest_expiry_date` for the
   * always-populated rollup, and `GET /inventory/{id}/batches` for the real breakdown). */
  batch_number: string | null;
  expiry_date: ISODateString | null;
  /** Soonest expiry_date across this item's active (qty remaining > 0) batches — populated
   * regardless of how many batches exist, unlike `expiry_date` above. */
  nearest_expiry_date: ISODateString | null;
  allow_negative_stock: boolean;
  category: string | null;
  unit_of_measure: string | null;
  supplier_id: UUID | null;
  version: number;
  is_deleted: boolean;
}

export interface InventoryItemCreate {
  branch_id: UUID;
  sku: string;
  name: string;
  description?: string | null;
  retail_price: DecimalString;
  wholesale_price?: DecimalString | null;
  current_stock?: DecimalString;
  low_stock_threshold?: DecimalString;
  batch_number?: string | null;
  expiry_date?: ISODateString | null;
  allow_negative_stock?: boolean;
  category?: string | null;
  unit_of_measure?: string | null;
  supplier_id?: UUID | null;
}

export interface InventoryItemUpdate {
  name?: string | null;
  description?: string | null;
  retail_price?: DecimalString | null;
  wholesale_price?: DecimalString | null;
  low_stock_threshold?: DecimalString | null;
  allow_negative_stock?: boolean | null;
  batch_number?: string | null;
  expiry_date?: ISODateString | null;
  category?: string | null;
  unit_of_measure?: string | null;
  supplier_id?: UUID | null;
  version: number;
}

export interface StockLogOut {
  id: UUID;
  inventory_item_id: UUID;
  change_quantity: DecimalString;
  reason: string | null;
  source_event_id: UUID | null;
  note: string | null;
  created_at: ISODateTimeString;
}

export interface BarcodeOut {
  id: UUID;
  inventory_item_id: UUID;
  code: string;
  is_primary: boolean;
}

export interface BarcodeCreate {
  code: string;
  is_primary?: boolean;
}

export interface WriteOffRequest {
  quantity: DecimalString;
  reason: string;
  /** Multi-batch tracking — name exactly which lot spoiled/was damaged. Omitted, the
   * write-off draws FEFO across whatever batches this item has, same as a sale. */
  batch_id?: UUID | null;
}

export interface WriteOffOut {
  item: InventoryItemOut;
  stock_log: StockLogOut;
}

// ---------------------------------------------------------------------------
// Purchase orders & stock transfers — backend/app/schemas/purchasing.py
// ---------------------------------------------------------------------------

export interface PurchaseOrderLineCreate {
  inventory_item_id: UUID;
  quantity_ordered: DecimalString;
  unit_cost: DecimalString;
}

export interface PurchaseOrderCreate {
  branch_id: UUID;
  supplier_id?: UUID | null;
  notes?: string | null;
  lines: PurchaseOrderLineCreate[];
}

export interface PurchaseOrderLineOut {
  id: UUID;
  inventory_item_id: UUID;
  quantity_ordered: DecimalString;
  quantity_received: DecimalString;
  unit_cost: DecimalString;
}

export type PurchaseOrderStatus = "DRAFT" | "ORDERED" | "PARTIALLY_RECEIVED" | "RECEIVED" | "CANCELLED";

export interface PurchaseOrderOut {
  id: UUID;
  branch_id: UUID;
  supplier_id: UUID | null;
  status: PurchaseOrderStatus | string;
  notes: string | null;
  lines: PurchaseOrderLineOut[];
}

export interface ReceiveLine {
  line_id: UUID;
  quantity_received: DecimalString;
  /** Multi-batch tracking — all optional, so a receipt that doesn't care about batching
   * behaves exactly as before this existed. `unit_cost`, if given, overrides the PO line's
   * own negotiated cost for THIS batch only. */
  batch_number?: string | null;
  expiry_date?: ISODateString | null;
  unit_cost?: DecimalString | null;
}

export interface ReceivePurchaseOrderRequest {
  lines: ReceiveLine[];
}

export interface StockTransferCreate {
  from_branch_id: UUID;
  to_branch_id: UUID;
  inventory_item_id: UUID;
  quantity: DecimalString;
}

export interface StockTransferOut {
  id: UUID;
  from_branch_id: UUID;
  to_branch_id: UUID;
  inventory_item_id: UUID;
  quantity: DecimalString;
  status: string;
}

// ---------------------------------------------------------------------------
// Reports — backend/app/schemas/reports.py
// ---------------------------------------------------------------------------

/** One row per BATCH approaching expiry (multi-batch tracking), not one vague row per item
 * — `id` is the batch id (pass as `WriteOffRequest.batch_id`), `inventory_item_id`
 * identifies the product, `quantity_remaining` is that SAME batch's own balance. */
export interface ExpiringSoonItemOut {
  id: UUID;
  inventory_item_id: UUID;
  branch_id: UUID;
  sku: string;
  name: string;
  quantity_remaining: DecimalString;
  batch_number: string | null;
  expiry_date: ISODateString;
  days_until_expiry: number;
}

// ---------------------------------------------------------------------------
// Multi-batch/lot inventory tracking — backend/app/schemas/inventory.py
// (InventoryBatchOut/Create/Correction), app/services/batch_service.py
// ---------------------------------------------------------------------------

export interface InventoryBatchOut {
  id: UUID;
  inventory_item_id: UUID;
  batch_number: string | null;
  expiry_date: ISODateString | null;
  unit_cost: DecimalString | null;
  quantity_received: DecimalString;
  quantity_remaining: DecimalString;
  source: "PURCHASE_ORDER" | "MANUAL" | "TRANSFER_IN" | "RETURN" | "BULK_IMPORT" | "MIGRATION" | "MANUAL_CORRECTION" | string;
  source_ref_id: UUID | null;
  received_at: ISODateTimeString;
  version: number;
}

/** `POST /inventory/{id}/batches` — manual batch receipt outside a purchase order. */
export interface InventoryBatchCreate {
  quantity: DecimalString;
  batch_number?: string | null;
  expiry_date?: ISODateString | null;
  unit_cost?: DecimalString | null;
}

/** `PATCH /inventory/{id}/batches/{batch_id}` — corrects identifying fields WITHOUT moving
 * stock (typo fix). Use `InventoryBatchCreate` (a new receipt) or a write-off to move
 * `quantity_remaining`. */
export interface InventoryBatchCorrection {
  batch_number?: string | null;
  expiry_date?: ISODateString | null;
  unit_cost?: DecimalString | null;
}

export interface LowStockItemOut {
  id: UUID;
  branch_id: UUID;
  sku: string;
  name: string;
  current_stock: DecimalString;
  low_stock_threshold: DecimalString;
}

export interface TopSellerOut {
  inventory_item_id: UUID;
  sku: string;
  name: string;
  quantity_sold: DecimalString;
  revenue: DecimalString;
}

export interface SalesSummaryOut {
  branch_id: UUID | null;
  date_from: ISODateString | null;
  date_to: ISODateString | null;
  sale_count: number;
  revenue_total: DecimalString;
  average_basket_size: DecimalString;
  top_sellers_by_quantity: TopSellerOut[];
  top_sellers_by_revenue: TopSellerOut[];
}

export interface PnlLineOut {
  account_code: string;
  account_name: string;
  account_type: AccountType | string;
  total: DecimalString;
}

export interface PnlOut {
  date_from: ISODateString | null;
  date_to: ISODateString | null;
  revenue_total: DecimalString;
  expense_total: DecimalString;
  net_income: DecimalString;
  revenue_lines: PnlLineOut[];
  expense_lines: PnlLineOut[];
}

export interface SalesByDayOut {
  day: ISODateString;
  revenue_total: DecimalString;
  sale_count: number;
}

/** `GET /reports/dashboard-summary` — backend/app/schemas/reports.py::DashboardSummaryOut.
 * Business-owner walkthrough follow-up: the new `/dashboard` home page's single data
 * source. `stock_value_retail` is `sum(current_stock * retail_price)` — a deliberate
 * retail-price proxy, not a true cost value (InventoryItem has no cost-basis field). */
export interface DashboardSummaryOut {
  branch_id: UUID | null;
  date_from: ISODateString | null;
  date_to: ISODateString | null;
  revenue_total: DecimalString;
  expense_total: DecimalString;
  net_income: DecimalString;
  stock_value_retail: DecimalString;
  low_stock_count: number;
  active_staff_count: number;
  sales_by_day: SalesByDayOut[];
  top_sellers_by_revenue: TopSellerOut[];
}

/** `GET /reports/sales-expense-summary` — backend/app/schemas/reports.py::PeriodSummaryOut.
 * `expense_total` is prorated for recurring expenses (spread across the interval each
 * occurrence covers) rather than a raw sum of `Expense.amount` rows dated in range — see
 * `reports_service.sales_expense_summary`'s docstring for the "why". */
export interface PeriodSummaryOut {
  key: string;
  label: string;
  date_from: ISODateString;
  date_to: ISODateString;
  sales_total: DecimalString;
  expense_total: DecimalString;
  net_total: DecimalString;
}

export interface SalesExpenseSummaryOut {
  periods: PeriodSummaryOut[];
}

// ---------------------------------------------------------------------------
// Expenses — backend/app/schemas/expenses.py (business-owner walkthrough
// follow-up: simplified-dashboard "Add Expense", posts to the ledger).
// ---------------------------------------------------------------------------

export const EXPENSE_CATEGORIES = ["RENT", "SALARIES", "UTILITIES", "SUPPLIES", "OTHER"] as const;
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

export interface ExpenseCreate {
  branch_id?: UUID | null;
  category: ExpenseCategory;
  description: string;
  amount: string;
  expense_date: string;
  is_recurring?: boolean;
  recurrence_interval?: string | null;
}

export interface ExpenseOut {
  id: UUID;
  branch_id: UUID | null;
  category: ExpenseCategory | string;
  description: string;
  amount: DecimalString;
  expense_date: ISODateString;
  is_recurring: boolean;
  recurrence_interval: string | null;
  ledger_entry_id: UUID;
  created_by_user_id: UUID;
  created_at: ISODateString;
}

// ---------------------------------------------------------------------------
// Sync conflicts — backend/app/schemas/sync.py (SyncConflictOut/ResolveRequest)
// ---------------------------------------------------------------------------

export interface SyncConflictOut {
  id: UUID;
  event_id: UUID;
  entity_type: string;
  entity_id: UUID;
  status: string;
  reason: string | null;
  payload: Record<string, unknown> | null;
  resolved: boolean;
  resolved_at: ISODateTimeString | null;
  created_at: ISODateTimeString;
}

export interface SyncConflictResolveRequest {
  status: "RESOLVED" | "DISMISSED";
  resolution_note?: string | null;
}

// ---------------------------------------------------------------------------
// Ledger — extends the stale shared-types LedgerEntryOut with `created_at`
// (backend/app/schemas/ledger.py, §10.5's fix).
// ---------------------------------------------------------------------------

export interface LegOut {
  id: UUID;
  account_id: UUID;
  debit: DecimalString;
  credit: DecimalString;
}

export interface LedgerEntryOut {
  id: UUID;
  branch_id: UUID | null;
  description: string | null;
  reference_source: string | null;
  legs: LegOut[];
  created_at: ISODateTimeString;
}

// ---------------------------------------------------------------------------
// Invoices — extends the stale shared-types InvoiceOrBill shapes with `customer_id`
// (§10.3, backend/app/schemas/invoices.py).
// ---------------------------------------------------------------------------

export interface InvoiceOrBillCreate {
  branch_id?: UUID | null;
  type: InvoiceOrBillType;
  contact_name: string;
  amount_due: DecimalString;
  due_date: ISODateString;
  is_recurring?: boolean;
  recurrence_interval?: RecurrenceInterval | null;
  customer_id?: UUID | null;
}

export interface InvoiceOrBillOut {
  id: UUID;
  branch_id: UUID | null;
  type: InvoiceOrBillType;
  contact_name: string;
  amount_due: DecimalString;
  amount_paid: DecimalString;
  status: InvoiceOrBillStatus;
  due_date: ISODateString;
  is_recurring: boolean;
  recurrence_interval: RecurrenceInterval | null;
  customer_id: UUID | null;
}

// ---------------------------------------------------------------------------
// 2026-07-13 five-branch integration pass — everything below is new local-type
// coverage for `backend/BACKEND_ARCHITECTURE.md` §11-§15's routes, verified directly
// against `backend/app/schemas/*.py` / `backend/app/api/v1/*.py` (not the doc summary).
// Same "declare locally, don't touch shared-types" rule as above (three other agents —
// pos-pwa, ecommerce-store — are concurrently building against this exact same backend
// in their own worktrees).
// ---------------------------------------------------------------------------

// --- Sales (extended) — backend/app/schemas/sales.py. shared-types' SaleOut/SaleCreate
// are stale post-integration (missing customer_id/currency/promo_code/loyalty fields,
// and tax_total is no longer client-suppliable). Import SaleOut/SaleItemOut from HERE
// wherever this pass's fields are needed (e.g. Returns' sale picker); the online-orders
// queue and other pre-existing call sites keep using the shared-types shape unchanged.
export interface SaleItemOut {
  id: UUID;
  inventory_item_id: UUID;
  quantity: DecimalString;
  unit_price: DecimalString;
  total_price: DecimalString;
}

export interface SaleOut {
  id: UUID;
  branch_id: UUID;
  user_id: UUID;
  source: string;
  sub_total: DecimalString;
  tax_total: DecimalString;
  discount_total: DecimalString;
  grand_total: DecimalString;
  payment_status: string;
  customer_id: UUID | null;
  currency: string | null;
  exchange_rate_to_tenant_currency: DecimalString | null;
  items: SaleItemOut[];
}

// ---------------------------------------------------------------------------
// Permissions & role defaults — backend/app/schemas/permission.py,
// api/v1/permissions.py (GET /permissions, any authenticated user),
// api/v1/roles.py (GET /roles/{role}/permissions, OWNER-only),
// api/v1/users.py (GET/PATCH /users/{id}/permissions, OWNER-only) — §11.
// ---------------------------------------------------------------------------

export interface PermissionOut {
  id: UUID;
  code: string;
  description: string;
  category: string | null;
}

export interface RoleDefaultPermissionsOut {
  role: string;
  permission_codes: string[];
  is_tenant_specific: boolean;
}

export interface UserPermissionOverrideOut {
  permission_code: string;
  granted: boolean;
}

export interface UserPermissionsOut {
  user_id: UUID;
  role: string;
  effective_permission_codes: string[];
  overrides: UserPermissionOverrideOut[];
}

export interface UserPermissionOverridePatch {
  permission_code: string;
  /** `null` removes the override entirely (reverts to the role default) — distinct from
   * `false`, which explicitly revokes a permission the role default would otherwise grant. */
  granted: boolean | null;
}

export interface UserPermissionsPatchRequest {
  overrides: UserPermissionOverridePatch[];
}

// ---------------------------------------------------------------------------
// Approval requests & rules — backend/app/schemas/approvals.py,
// api/v1/approval_requests.py, api/v1/approval_rules.py — §11.2. Gated by
// `require_permission("purchase_order.approve" | "approval_rule.manage")`, NOT
// `require_roles` — platform-default seed grants both to OWNER only, but a tenant can
// grant either to another role via a per-user override (see UserPermissionsOut above).
// ---------------------------------------------------------------------------

/** `ApprovalRequest.status` — distinct from `PurchaseOrder.status`'s "PENDING_APPROVAL"
 * literal (a different model/column entirely; don't confuse the two). */
export type ApprovalRequestStatus = "PENDING" | "APPROVED" | "REJECTED";

export interface ApprovalRequestOut {
  id: UUID;
  entity_type: string;
  entity_id: UUID;
  rule_id: UUID | null;
  requested_by_user_id: UUID;
  status: ApprovalRequestStatus | string;
  approved_by_user_id: UUID | null;
  decided_at: ISODateTimeString | null;
  note: string | null;
  created_at: ISODateTimeString;
}

export interface ApprovalDecisionRequest {
  note?: string | null;
}

export interface ApprovalRuleCreate {
  /** Defaults to "PURCHASE_ORDER" server-side — the only entity type this backend pass
   * supports (`ApprovalEntityType` has exactly one member). */
  entity_type?: string;
  min_amount: DecimalString;
  required_role?: string | null;
  required_permission_code?: string | null;
}

export interface ApprovalRuleOut {
  id: UUID;
  entity_type: string;
  min_amount: DecimalString;
  required_role: string | null;
  required_permission_code: string | null;
  is_active: boolean;
}

// ---------------------------------------------------------------------------
// Tax rates & exchange rates — backend/app/schemas/tax.py, fx.py — §13.1/§13.2.
// ---------------------------------------------------------------------------

export interface TaxRateCreate {
  /** `null`/omitted = tenant-wide default rate; set = branch-specific override. */
  branch_id?: UUID | null;
  name: string;
  rate_percent: DecimalString;
  is_inclusive?: boolean;
  is_active?: boolean;
  effective_from?: ISODateString | null;
  effective_to?: ISODateString | null;
}

export interface TaxRateUpdate {
  name?: string;
  rate_percent?: DecimalString;
  is_inclusive?: boolean;
  is_active?: boolean;
  effective_from?: ISODateString | null;
  effective_to?: ISODateString | null;
  branch_id?: UUID | null;
}

export interface TaxRateOut {
  id: UUID;
  branch_id: UUID | null;
  name: string;
  rate_percent: DecimalString;
  is_inclusive: boolean;
  is_active: boolean;
  effective_from: ISODateString | null;
  effective_to: ISODateString | null;
  is_deleted: boolean;
}

export interface ExchangeRateCreate {
  from_currency: string;
  to_currency: string;
  rate: DecimalString;
  /** Defaults to "now" server-side if omitted. */
  effective_at?: ISODateTimeString | null;
}

export interface ExchangeRateOut {
  id: UUID;
  from_currency: string;
  to_currency: string;
  rate: DecimalString;
  effective_at: ISODateTimeString;
  is_deleted: boolean;
}

// ---------------------------------------------------------------------------
// Bank reconciliation — backend/app/schemas/bank.py — §13.3. All routes ACCOUNTANT-only.
// ---------------------------------------------------------------------------

export interface BankStatementLineOut {
  id: UUID;
  branch_id: UUID | null;
  account_id: UUID;
  external_reference: string | null;
  description: string | null;
  /** Signed: positive = deposit, negative = withdrawal. */
  amount: DecimalString;
  transaction_date: ISODateString;
  is_matched: boolean;
  matched_transaction_leg_id: UUID | null;
  created_at: ISODateTimeString;
}

export interface ImportErrorOut {
  row_number: number;
  reason: string;
}

export interface CsvImportResponse {
  imported_count: number;
  lines: BankStatementLineOut[];
  errors: ImportErrorOut[];
}

export interface CandidateLegOut {
  id: UUID;
  ledger_entry_id: UUID;
  account_id: UUID;
  debit: DecimalString;
  credit: DecimalString;
  created_at: ISODateTimeString;
}

export interface UnmatchedLineWithCandidatesOut {
  line: BankStatementLineOut;
  candidates: CandidateLegOut[];
}

export interface MatchRequest {
  bank_statement_line_id: UUID;
  transaction_leg_id: UUID;
}

// ---------------------------------------------------------------------------
// Shipments — backend/app/schemas/shipping.py — §14.1.
// ---------------------------------------------------------------------------

export type ShipmentStatus = "PENDING" | "PACKED" | "SHIPPED" | "DELIVERED" | "FAILED";

export interface ShipmentCreate {
  /** Exactly one of `sale_id`/`invoice_or_bill_id` must be set (backend model-validator). */
  sale_id?: UUID | null;
  invoice_or_bill_id?: UUID | null;
  /** Defaults to the linked Sale's/Invoice's branch if omitted. */
  branch_id?: UUID | null;
  recipient_name: string;
  address_line1: string;
  address_line2?: string | null;
  city: string;
  region?: string | null;
  postal_code?: string | null;
  country: string;
  phone?: string | null;
  /** Was missing entirely — `Shipment.contact_email` (backend `shipping.py`, migration
   * `0014`) exists specifically so a guest/customer checkout order has a follow-up email
   * on file, but this type never surfaced it, so a staff-created shipment had no way to
   * record one either. */
  contact_email?: string | null;
  /** Only "MANUAL" is registered server-side in this backend pass. */
  carrier?: string;
  weight?: DecimalString | null;
}

export interface ShipmentUpdate {
  status?: ShipmentStatus | string;
  tracking_number?: string;
}

export interface ShipmentOut {
  id: UUID;
  branch_id: UUID | null;
  sale_id: UUID | null;
  invoice_or_bill_id: UUID | null;
  recipient_name: string;
  address_line1: string;
  address_line2: string | null;
  city: string;
  region: string | null;
  postal_code: string | null;
  country: string;
  phone: string | null;
  contact_email: string | null;
  status: ShipmentStatus | string;
  carrier: string;
  tracking_number: string | null;
  rate_amount: DecimalString;
  shipped_at: ISODateTimeString | null;
  delivered_at: ISODateTimeString | null;
  created_at: ISODateTimeString;
}

/** `GET /shipments/{id}/status-history` — one row per status transition (including the
 * initial status at creation), each with a real timestamp (`created_at`). Closes the
 * "when did this actually go PENDING -> PACKED" gap `ShipmentOut` alone can't answer —
 * see `backend/app/models/shipping.py::ShipmentStatusEvent`'s doc comment. */
export interface ShipmentStatusEventOut {
  id: UUID;
  status: ShipmentStatus | string;
  /** `null` for a guest/public-checkout-created shipment's initial event — nobody in
   * `users` placed it. */
  changed_by_user_id: UUID | null;
  created_at: ISODateTimeString;
}

// ---------------------------------------------------------------------------
// Returns — backend/app/schemas/returns.py — §15.1.
// ---------------------------------------------------------------------------

export interface ReturnLineCreate {
  /** A `SaleItem.id` (from the picked Sale's `items[]`), not an inventory_item_id. */
  sale_item_id: UUID;
  /** Delta for THIS return, not cumulative — the backend tracks already-returned qty per
   * sale_item_id across existing returns and rejects (409) an over-return. */
  quantity: DecimalString;
}

export interface ReturnCreate {
  sale_id: UUID;
  reason?: string | null;
  lines: ReturnLineCreate[];
}

export interface ReturnLineOut {
  id: UUID;
  sale_item_id: UUID;
  inventory_item_id: UUID;
  quantity: DecimalString;
  unit_price: DecimalString;
  total_price: DecimalString;
}

export interface ReturnOut {
  id: UUID;
  sale_id: UUID;
  branch_id: UUID | null;
  user_id: UUID;
  reason: string | null;
  refund_total: DecimalString;
  lines: ReturnLineOut[];
}

// ---------------------------------------------------------------------------
// Promo codes, gift cards, loyalty — backend/app/schemas/promotions.py,
// tenant_settings.py — §15.2/§15.3/§15.4.
// ---------------------------------------------------------------------------

export type PromoCodeType = "PERCENTAGE" | "FIXED_AMOUNT";

export interface PromoCodeCreate {
  code: string;
  type: PromoCodeType;
  value: DecimalString;
  expiry_date?: ISODateString | null;
  usage_limit?: number | null;
  min_purchase_amount?: DecimalString | null;
  is_active?: boolean;
}

export interface PromoCodeOut {
  id: UUID;
  code: string;
  type: PromoCodeType | string;
  value: DecimalString;
  expiry_date: ISODateString | null;
  usage_limit: number | null;
  min_purchase_amount: DecimalString | null;
  is_active: boolean;
  created_at: ISODateTimeString;
}

export interface GiftCardIssueRequest {
  /** Server auto-generates `GC-XXXXXXXXXXXX` if omitted. */
  code?: string | null;
  initial_balance: DecimalString;
  branch_id?: UUID | null;
}

export interface GiftCardOut {
  id: UUID;
  code: string;
  initial_balance: DecimalString;
  current_balance: DecimalString;
  is_active: boolean;
  created_at: ISODateTimeString;
}

export interface GiftCardRedeemRequest {
  sale_id: UUID;
  amount: DecimalString;
  branch_id?: UUID | null;
}

export interface LoyaltyAccountOut {
  id: UUID;
  customer_id: UUID;
  points_balance: DecimalString;
}

export interface TenantLoyaltySettingsOut {
  loyalty_points_per_currency_unit: DecimalString;
  loyalty_point_redemption_value: DecimalString;
}

export interface TenantLoyaltySettingsUpdate {
  loyalty_points_per_currency_unit: DecimalString;
  loyalty_point_redemption_value: DecimalString;
}

/** `GET/PATCH /tenant-settings/ecommerce` — the storefront on/off switch behind
 * `Tenant.ecommerce_enabled`. Defaults true for a new tenant (migration 0017); PATCH is
 * OWNER-only server-side. */
export interface TenantEcommerceSettingsOut {
  ecommerce_enabled: boolean;
}

export interface TenantEcommerceSettingsUpdate {
  ecommerce_enabled: boolean;
}

// ---------------------------------------------------------------------------
// Onboarding checklist — backend/app/schemas/onboarding.py — §9.2's 6-step
// "getting started" guide, computed live (no backing table).
// ---------------------------------------------------------------------------

export interface OnboardingChecklistStepOut {
  step: string;
  label: string;
  completed: boolean;
  completed_at: string | null;
  current: number | null;
  target: number | null;
}

export interface OnboardingChecklistOut {
  tenant_id: string;
  steps: OnboardingChecklistStepOut[];
  all_complete: boolean;
}

// ---------------------------------------------------------------------------
// Notifications & webhooks — backend/app/schemas/notification.py, webhook.py — §12.5/
// §12.6. All routes OWNER-only.
// ---------------------------------------------------------------------------

export interface NotificationLogOut {
  id: UUID;
  channel: string;
  to: string;
  subject: string | null;
  body: string;
  status: string;
  created_at: ISODateTimeString;
}

export interface WebhookSubscriptionCreate {
  target_url: string;
  event_types: string[];
}

export interface WebhookSubscriptionOut {
  id: UUID;
  target_url: string;
  event_types: string[];
  is_active: boolean;
  created_at: ISODateTimeString;
}

export interface WebhookSubscriptionCreateResponse extends WebhookSubscriptionOut {
  /** Shown exactly once — never re-displayed by `GET /webhooks/subscriptions`. */
  secret: string;
  note: string;
}

export interface WebhookDeliveryOut {
  id: UUID;
  subscription_id: UUID;
  event_type: string;
  payload: Record<string, unknown>;
  status: string;
  attempt_count: number;
  last_attempted_at: ISODateTimeString | null;
  created_at: ISODateTimeString;
}

// ---------------------------------------------------------------------------
// Inventory bulk import/export & serialized units — backend/app/schemas/inventory.py
// — §14.2/§14.4. Extends (does not replace) InventoryItemOut/Create/Update above with
// fields the integration pass added to InventoryItem: tax_rate_id, is_serialized.
// ---------------------------------------------------------------------------

export interface InventoryItemOutExt extends InventoryItemOut {
  tax_rate_id: UUID | null;
  is_serialized: boolean;
  /** Whether this item shows up in `GET /public/catalog` — defaults true on
   * the backend (`backend/app/models/inventory.py`); an OWNER/ECOM_MGR can
   * flip it off per item to hide an in-progress/POS-only SKU from the
   * storefront without deleting it. */
  is_ecommerce_visible: boolean;
  /** Free-form key/value attributes not covered by the fixed column set —
   * see `backend/app/models/inventory.py`'s `custom_fields` comment. */
  custom_fields: Record<string, string>;
}

export interface InventoryItemCreateExt extends InventoryItemCreate {
  tax_rate_id?: UUID | null;
  is_serialized?: boolean;
  is_ecommerce_visible?: boolean;
  custom_fields?: Record<string, string>;
}

export interface InventoryItemUpdateExt extends InventoryItemUpdate {
  tax_rate_id?: UUID | null;
  is_serialized?: boolean | null;
  is_ecommerce_visible?: boolean | null;
  custom_fields?: Record<string, string> | null;
}

// ---------------------------------------------------------------------------
// Product images — backend/app/schemas/images.py
// ---------------------------------------------------------------------------

export interface ProductImageOut {
  id: UUID;
  inventory_item_id: UUID;
  image_hash: string;
  is_primary: boolean;
  sort_order: number;
  /** Root-relative public paths (e.g. `/img/{tenant_id}/...`) — prefix with
   * `API_ROOT_URL` (`src/env.ts`) to build a real `<img src>`. */
  original_path: string;
  medium_path: string;
  thumb_path: string;
}

export interface InventoryImportRowError {
  /** 1-based, header excluded. */
  row: number;
  message: string;
}

export interface InventoryImportResult {
  created: number;
  updated: number;
  errors: InventoryImportRowError[];
}

/** `SerializedUnit.status` — plain string column, no formal enum class server-side. */
export type SerializedUnitStatus = "IN_STOCK" | "SOLD" | "RETURNED" | "DAMAGED";

export interface SerializedUnitCreate {
  serial_numbers: string[];
}

export interface SerializedUnitOut {
  id: UUID;
  inventory_item_id: UUID;
  serial_number: string;
  status: SerializedUnitStatus | string;
  sale_item_id: UUID | null;
  created_at: ISODateTimeString;
}

// ---------------------------------------------------------------------------
// Purchase orders — extends the PurchaseOrderOut above with the "PENDING_APPROVAL"
// status the integration pass's approval workflow can now set, plus the
// from-low-stock auto-reorder request — backend/app/schemas/purchasing.py — §11.2/§14.12.
// ---------------------------------------------------------------------------

export type PurchaseOrderStatusExt = "DRAFT" | "PENDING_APPROVAL" | "ORDERED" | "PARTIALLY_RECEIVED" | "RECEIVED" | "CANCELLED";

export interface PurchaseOrderFromLowStockRequest {
  branch_id: UUID;
  supplier_id?: UUID | null;
}

// ---------------------------------------------------------------------------
// Branding & custom domains (§11) — backend/app/schemas/branding.py. Real API
// as of this pass; replaces the `MOCK_TENANT_BRANDING` placeholder this app
// used to theme itself from unconditionally.
// ---------------------------------------------------------------------------

export interface TenantBrandingOut {
  tenant_id: UUID;
  slug: string;
  logo_path: string | null;
  favicon_path: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  accent_color: string | null;
  display_name: string | null;
  address: string | null;
  phone: string | null;
  tin: string | null;
  custom_fields: Record<string, string>;
  updated_by_user_id: UUID | null;
  updated_by_operator_id: UUID | null;
  updated_at: ISODateTimeString;
}

export interface TenantBrandingUpdate {
  primary_color?: string | null;
  secondary_color?: string | null;
  accent_color?: string | null;
  display_name?: string | null;
  address?: string | null;
  phone?: string | null;
  tin?: string | null;
  custom_fields?: Record<string, string> | null;
}

export interface BrandingAssetOut {
  asset_type: "logo" | "favicon";
  path: string;
}

export type TenantDomainTargetApp = "DASHBOARD_ADMIN" | "ECOMMERCE_STORE";

export interface TenantDomainOut {
  id: UUID;
  tenant_id: UUID;
  domain: string;
  target_app: TenantDomainTargetApp | string;
  verification_token: string;
  verified: boolean;
  tls_status: string;
  created_at: ISODateTimeString;
  verification_dns_record: string;
}

export interface TenantDomainCreate {
  domain: string;
  target_app: TenantDomainTargetApp;
}
